import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  InputAdornment,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  Tooltip,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  Receipt as ReceiptIcon,
  Refresh,
  Person,
  ArrowBack
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { recebimentoService, clienteService } from '../../services/api';
import ReciboAtendimento from './ReciboAtendimento';

// Configuração da API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://trajetus.net';

// =====================================================
// INTERFACES TYPESCRIPT
// =====================================================

interface Cliente {
  id: number;
  nome: string;
  email?: string;
  celular?: string;
  foto?: string;
  ativo: boolean;
  id_usuario: number;
}

interface Recebimento {
  id: number;
  id_cliente: number;
  id_usuario?: number;
  descricao: string;
  valor: number;
  data_vencimento: string;
  data_recebimento?: string;
  forma_pagamento?: string;
  status: 'pendente' | 'pago' | 'vencido' | 'cancelado';
  observacoes?: string;
  ativo: boolean;
  data_cadastro: string;
  ultima_atualizacao?: string;
  cliente_nome?: string;
  cliente_email?: string;
  cliente_celular?: string;
  cliente_foto?: string;
  usuario_nome?: string;
}

interface FormData {
  id_cliente: number | '';
  descricao: string;
  data_recebimento: string;
  data_vencimento: string;
  valor: number | '';
  status: 'pendente' | 'pago' | 'vencido' | 'cancelado';
  forma_pagamento: string;
  observacoes: string;
}

// =====================================================
// FUNÇÕES AUXILIARES
// =====================================================

const getDataAtual = (): string => {
  return new Date().toISOString().split('T')[0];
};

const formatarMoeda = (valor: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valor);
};

const formatarData = (data: string): string => {
  if (!data) return '';
  
  if (data.includes('T')) {
    data = data.split('T')[0];
  }
  
  const [ano, mes, dia] = data.split('-');
  const dataLocal = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
  
  return dataLocal.toLocaleDateString('pt-BR');
};

const mapearRecebimentosComClientes = (dados: any[], listaClientes: Cliente[]): Recebimento[] => {
  return (dados || []).map((rec: any) => {
    const clienteRelacionado = listaClientes.find(c => Number(c.id) === Number(rec.id_cliente));
    const nomeCliente = clienteRelacionado?.nome || rec.cliente_nome || 'Cliente não informado';
    return {
      ...rec,
      cliente_nome: nomeCliente
    };
  });
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const Recebimentos: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estados principais
  const [recebimentos, setRecebimentos] = useState<Recebimento[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Estados da interface
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Estados de filtros
  const [filtros, setFiltros] = useState({
    cliente: '',
    status: '',
    dataInicio: '',
    dataFim: ''
  });

  // Estado do formulário
  const [formData, setFormData] = useState<FormData>({
    id_cliente: '',
    descricao: '',
    data_recebimento: getDataAtual(),
    data_vencimento: getDataAtual(),
    valor: '',
    status: 'pendente',
    forma_pagamento: '',
    observacoes: ''
  });

  // Opções de status
  const statusOptions = [
    { value: 'pendente', label: 'Pendente', color: 'warning' as const },
    { value: 'pago', label: 'Pago', color: 'success' as const },
    { value: 'vencido', label: 'Vencido', color: 'error' as const },
    { value: 'cancelado', label: 'Cancelado', color: 'default' as const }
  ];

  // Opções de forma de pagamento
  const formasPagamento = [
    'Dinheiro',
    'PIX',
    'Cartão de Débito',
    'Cartão de Crédito',
    'Transferência Bancária',
    'Boleto',
    'Outros'
  ];

  // Estados do recibo
  const [reciboOpen, setReciboOpen] = useState(false);
  const [recebimentoParaRecibo, setRecebimentoParaRecibo] = useState<Recebimento | null>(null);

  // Função para emitir recibo
  const handleEmitirRecibo = (recebimento: Recebimento) => {
    setRecebimentoParaRecibo(recebimento);
    setReciboOpen(true);
  };

  // Função para fechar recibo
  const handleFecharRecibo = () => {
    setReciboOpen(false);
    setRecebimentoParaRecibo(null);
  };

  // =====================================================
  // EFEITOS
  // =====================================================

  useEffect(() => {
    carregarDados();
  }, []);

  // =====================================================
  // FUNÇÕES DE API
  // =====================================================

  const carregarDados = async () => {
    await carregarRecebimentos();
  };

  const carregarRecebimentos = async () => {
    try {
      setLoading(true);
      
      if (!user?.id) {
        setError('Usuário não está logado');
        return;
      }

      console.log('🔍 [RECEBIMENTOS] Carregando recebimentos para usuário:', user.id);

      let clientesAtuais = clientes;
      if (!clientesAtuais || clientesAtuais.length === 0) {
        clientesAtuais = await carregarClientes();
      }

      const response = await recebimentoService.buscarPorUsuario(user.id);
      console.log('🔍 [RECEBIMENTOS] Resposta recebida:', response);
      
      if (response.data?.success) {
        const dadosRecebimentos = response.data.data || [];
        const recebimentosComClientes = mapearRecebimentosComClientes(dadosRecebimentos, clientesAtuais || []);
        setRecebimentos(recebimentosComClientes);
        console.log('🔍 [RECEBIMENTOS] Recebimentos carregados:', recebimentosComClientes.length);
      } else {
        setError(response.data?.message || 'Erro ao carregar recebimentos');
      }
    } catch (error) {
      console.error('🔍 [RECEBIMENTOS] Erro ao carregar recebimentos:', error);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const carregarClientes = async (): Promise<Cliente[]> => {
    try {
      if (!user?.id) {
        console.log('🔍 [RECEBIMENTOS] Usuário não logado, não carregando clientes');
        return [];
      }

      console.log('🔍 [RECEBIMENTOS] Carregando clientes para usuário:', user.id);
      const response = await clienteService.listarPorUsuario(user.id);
      console.log('🔍 [RECEBIMENTOS] Resposta clientes:', response);
      
      if (response.data?.success) {
        // Filtrar apenas por ativo
        const clientesAtivos = response.data.data.filter((cliente: Cliente) => cliente.ativo);
        setClientes(clientesAtivos || []);
        console.log('🔍 [RECEBIMENTOS] Clientes ativos carregados:', clientesAtivos.length);
        return clientesAtivos || [];
      } else if (Array.isArray(response.data)) {
        const clientesAtivos = response.data.filter((cliente: Cliente) => cliente.ativo);
        setClientes(clientesAtivos || []);
        console.log('🔍 [RECEBIMENTOS] Clientes ativos carregados (fallback):', clientesAtivos.length);
        return clientesAtivos || [];
      }
    } catch (error) {
      console.error('🔍 [RECEBIMENTOS] Erro ao carregar clientes:', error);
      return [];
    }
    return [];
  };

  const salvarRecebimento = async () => {
    try {
      if (!formData.id_cliente || !formData.descricao || !formData.valor || 
          !formData.data_vencimento || !formData.data_recebimento) {
        setError('Preencha todos os campos obrigatórios');
        return;
      }

      setLoading(true);
      
      const dadosEnvio = {
        ...formData,
        id_usuario: user?.id
      };

      console.log('🔍 [RECEBIMENTOS] Salvando recebimento:', dadosEnvio);
      
      let response;
      if (editingId) {
        response = await recebimentoService.atualizar(editingId, dadosEnvio);
      } else {
        response = await recebimentoService.criar(dadosEnvio);
      }

      console.log('🔍 [RECEBIMENTOS] Resposta do salvamento:', response);

      if (response.data?.success) {
        setSuccess(editingId ? 'Recebimento atualizado com sucesso!' : 'Recebimento criado com sucesso!');
        handleCloseForm();
        carregarRecebimentos();
        
        // Fazer mensagem desaparecer após 5 segundos
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      } else {
        setError(response.data?.message || 'Erro ao salvar recebimento');
      }
    } catch (error) {
      console.error('🔍 [RECEBIMENTOS] Erro ao salvar recebimento:', error);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const excluirRecebimento = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este recebimento?')) {
      return;
    }

    try {
      setLoading(true);
      
      console.log('🔍 [RECEBIMENTOS] Excluindo recebimento ID:', id);
      const response = await recebimentoService.excluir(id);
      console.log('🔍 [RECEBIMENTOS] Resposta da exclusão:', response);

      if (response.data?.success) {
        setSuccess('Recebimento excluído com sucesso!');
        carregarRecebimentos();
        
        // Fazer mensagem desaparecer após 5 segundos
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      } else {
        setError(response.data?.message || 'Erro ao excluir recebimento');
      }
    } catch (error) {
      console.error('🔍 [RECEBIMENTOS] Erro ao excluir recebimento:', error);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FUNÇÕES DE CONTROLE
  // =====================================================

  const abrirFormulario = (recebimento?: Recebimento) => {
    if (recebimento) {
      setEditingId(recebimento.id);
      setFormData({
        id_cliente: recebimento.id_cliente,
        descricao: recebimento.descricao,
        data_recebimento: recebimento.data_recebimento ? recebimento.data_recebimento.split('T')[0] : getDataAtual(),
        data_vencimento: recebimento.data_vencimento.split('T')[0],
        valor: recebimento.valor,
        status: recebimento.status,
        forma_pagamento: recebimento.forma_pagamento || '',
        observacoes: recebimento.observacoes || ''
      });
    } else {
      setEditingId(null);
      setFormData({
        id_cliente: '',
        descricao: '',
        data_recebimento: getDataAtual(),
        data_vencimento: getDataAtual(),
        valor: '',
        status: 'pendente',
        forma_pagamento: '',
        observacoes: ''
      });
    }
    setShowForm(true);
    setError(null);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setError(null);
  };

  const limparFiltros = () => {
    setFiltros({
      cliente: '',
      status: '',
      dataInicio: '',
      dataFim: ''
    });
  };

  // =====================================================
  // FILTROS
  // =====================================================

  const recebimentosFiltrados = recebimentos.filter(recebimento => {
    const matchCliente = !filtros.cliente || recebimento.id_cliente.toString() === filtros.cliente;
    const matchStatus = !filtros.status || recebimento.status === filtros.status;
    
    const dataVencimento = recebimento.data_vencimento.split('T')[0];
    const matchDataInicio = !filtros.dataInicio || dataVencimento >= filtros.dataInicio;
    const matchDataFim = !filtros.dataFim || dataVencimento <= filtros.dataFim;

    return matchCliente && matchStatus && matchDataInicio && matchDataFim;
  });

  const getStatusColor = (status: string) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption?.color || 'default';
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (showForm) {
    // Tela de Formulário
    return (
      <Box sx={{ p: 3 }}>
        {/* Header do Formulário */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleCloseForm} color="primary">
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person color="primary" />
              {editingId ? 'Editar Recebimento' : 'Novo Recebimento'}
            </Typography>
          </Box>
        </Box>

        {/* Alertas */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Formulário */}
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Cliente */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={formData.id_cliente}
                  onChange={(e) => setFormData(prev => ({ ...prev, id_cliente: e.target.value as number }))}
                  label="Cliente"
                >
                  {clientes.map(cliente => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Descrição */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Descrição"
                value={formData.descricao}
                onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>

            {/* Data de Vencimento */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Data de Vencimento"
                value={formData.data_vencimento}
                onChange={(e) => setFormData(prev => ({ ...prev, data_vencimento: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Data de Quitação */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Data de Quitação"
                value={formData.data_recebimento}
                onChange={(e) => setFormData(prev => ({ ...prev, data_recebimento: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Valor */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Valor"
                type="number"
                value={formData.valor}
                onChange={(e) => setFormData(prev => ({ ...prev, valor: parseFloat(e.target.value) || '' }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  label="Status"
                >
                  {statusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Forma de Pagamento */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Forma de Pagamento</InputLabel>
                <Select
                  value={formData.forma_pagamento}
                  onChange={(e) => setFormData(prev => ({ ...prev, forma_pagamento: e.target.value }))}
                  label="Forma de Pagamento"
                >
                  {formasPagamento.map(forma => (
                    <MenuItem key={forma} value={forma}>
                      {forma}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Observações */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Observações"
                value={formData.observacoes}
                onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              />
            </Grid>
          </Grid>

          {/* Botões */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCloseForm}
              disabled={loading}
              size="large"
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              onClick={salvarRecebimento}
              disabled={loading}
              size="large"
            >
              {editingId ? 'Atualizar' : 'Salvar'}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Tela de Lista
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)} color="primary">
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            Recebimentos
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => abrirFormulario()}
          disabled={loading}
        >
          Novo Recebimento
        </Button>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="medium">
              <InputLabel>Cliente</InputLabel>
              <Select
                value={filtros.cliente}
                onChange={(e) => setFiltros(prev => ({ ...prev, cliente: e.target.value }))}
                label="Cliente"
              >
                <MenuItem value="">Todos</MenuItem>
                {clientes.map(cliente => (
                  <MenuItem key={cliente.id} value={cliente.id.toString()}>
                    {cliente.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel>Status</InputLabel>
              <Select
                value={filtros.status}
                onChange={(e) => setFiltros(prev => ({ ...prev, status: e.target.value }))}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                {statusOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="medium"
              type="date"
              label="Vencimento Inicial"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="medium"
              type="date"
              label="Vencimento Final"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={carregarDados}
                disabled={loading}
                startIcon={<Refresh />}
                size="medium"
                sx={{ height: '56px' }}
              >
                Atualizar
              </Button>
              <Button
                variant="outlined"
                onClick={limparFiltros}
                size="medium"
                sx={{ height: '56px' }}
              >
                Limpar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : recebimentosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {Object.values(filtros).some(f => f) ? 'Nenhum recebimento encontrado com os filtros aplicados' : 'Nenhum recebimento cadastrado'}
                  </TableCell>
                </TableRow>
              ) : (
                recebimentosFiltrados.map((recebimento, index) => (
                  <TableRow 
                    key={recebimento.id} 
                    hover
                    sx={{
                      height: '32px',
                      backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                    }}
                  >
                    <TableCell sx={{ py: 0.5 }}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {recebimento.cliente_nome}
                        </Typography>
                        {recebimento.cliente_email && (
                          <Typography variant="caption" color="text.secondary">
                            {recebimento.cliente_email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>{formatarData(recebimento.data_vencimento)}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{formatarMoeda(recebimento.valor)}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip
                        label={statusOptions.find(opt => opt.value === recebimento.status)?.label}
                        color={getStatusColor(recebimento.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Tooltip title="Emitir Recibo">
                        <IconButton
                          size="small"
                          onClick={() => handleEmitirRecibo(recebimento)}
                          disabled={loading}
                          sx={{ color: 'success.main' }}
                        >
                          <ReceiptIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => abrirFormulario(recebimento)}
                          disabled={loading}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => excluirRecebimento(recebimento.id)}
                          disabled={loading}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal de Recibo de Atendimento */}
      <ReciboAtendimento
        open={reciboOpen}
        onClose={handleFecharRecibo}
        recebimento={recebimentoParaRecibo}
      />
    </Box>
  );
};

export default Recebimentos;
